import {AfterViewInit, Component, EventEmitter, Input, NgZone, OnChanges, OnInit, Output, SecurityContext, ViewChild} from '@angular/core';
import {UntypedFormControl} from '@angular/forms';
import {ControlTemplate} from '../_interface/control-template.enum';
import {DynamicIdService} from 'shared/_services/dynamic.id.service';
import {Blur, Focus, QuillEditorComponent} from 'ngx-quill';
import {Observable} from 'rxjs';
import {take} from 'rxjs/operators';
import {QUILL_MODULES} from 'shared/quill-config';
import {isControlRequired} from '../functions/check-if-control-required.fn';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {DomSanitizer} from '@angular/platform-browser';
import Quill from 'quill';

@UntilDestroy()
@Component({
	selector: 'form-richtext',
	templateUrl: './richtext.component.html',
	styleUrls: ['./richtext.component.scss']
})
export class RichTextComponent implements OnInit, AfterViewInit, OnChanges {
	@Input()
	control: UntypedFormControl;

	@Input()
	id: string = this.dynamicIdService.getId('richtext');

	@Input()
	templateFieldId: string;

	@Input()
	label: string;

	@Input()
	placeholder: string;

	@Input()
	template: ControlTemplate = ControlTemplate.inline;

	@Input()
	spellcheck = true;

	@ViewChild('quillEditor')
	quillEditor: QuillEditorComponent;

	@Input() set setWarnings(warnings: {[key: string]: any}) {
		this.warnings = warnings;
	}

	modules = QUILL_MODULES;
	isFocused = false;
	quillReady = false;
	editorFocus$: Observable<Focus>;
	editorBlur$: Observable<Blur>;

	@Output() valueChange: EventEmitter<string> =  new EventEmitter<string>();
	ControlTemplate = ControlTemplate;
	isControlRequired: boolean;
	warnings: {[p: string]: any};

	constructor(private dynamicIdService: DynamicIdService, private sanitizer: DomSanitizer, private ngZone: NgZone) {}

	ngOnInit(): void {
		if (!this.control) throw new Error('control is required');
		this.isControlRequired = isControlRequired(this.control);
	}

	ngOnChanges(): void {
		if (this.quillReady) {
			this.applyContentToQuill();
		}
	}

	ngAfterViewInit(): void {
		if (!this.quillEditor) {
			throw new Error('Quill editor is not initialized');
		}
		this.editorBlur$ = this.quillEditor.onBlur;
		this.editorFocus$ = this.quillEditor.onFocus;

		this.editorFocus$.pipe(
			untilDestroyed(this)
		).subscribe((_: Focus) => {
			this.isFocused = true;
		});
		this.editorBlur$.pipe(
			untilDestroyed(this)
		).subscribe((_: Blur) => {
			this.isFocused = false;
		});
	}

	phraseSelect = ($event: any) => {
		const quill = this.quillEditor.quillEditor;
		if (quill) {
			const sanitizedContent = this.sanitizer.sanitize(SecurityContext.HTML, $event.content);
			if (sanitizedContent) {
				this.control.patchValue((this.control.value || '') + sanitizedContent);
				const length = quill.getLength();
				quill.clipboard.dangerouslyPasteHTML(length - 1, this.control.value);
				this.control.markAsDirty();
				this.control.markAsTouched();
			}
		}
	}

	onEditorCreated(quillCreated: Quill): void {
		this.quillEditor.quillEditor = quillCreated;
		this.quillReady = true;
		this.applyContentToQuill();
	}

	applyContentToQuill(): void {
		const quill = this.quillEditor?.quillEditor;
		if (!quill) return;
		const value = this.control.value || '';
		this.ngZone.onStable.pipe(take(1)).subscribe(() => {
			if (quill.getLength() <= 1 && value) {
				const delta = quill.clipboard.convert({ html: value, text: '' });
				quill.setContents(delta, 'silent');
			}
		});
	}
}